<template>
    <LoadingComponent :props="loading" />
    <div class="col-12">
        <div class="db-card">
            <div class="db-card-header border-none">
                <h3 class="db-card-title pt-1">{{ $t('menu.products') }}</h3>
                <div class="db-card-filter">
                    <TableLimitComponent :method="list" :search="props.search" :page="paginationPage" />
                    <FilterComponent @click.prevent="handleSlide('product-filter')" />
                    <div class="dropdown-group">
                        <ExportComponent />
                        <div class="dropdown-list db-card-filter-dropdown-list">
                            <PrintComponent :props="printObj" />
                            <ExcelComponent :method="xls" />
                        </div>
                    </div>
                    <div class="dropdown-group">
                        <ImportComponent />
                        <div class="dropdown-list db-card-filter-dropdown-list">
                            <SampleFileComponent :method="download" />
                            <UploadFileComponent props="productFileUloadModal" />
                        </div>
                    </div>

                    <!-- DROPDOWN ACTUALIZACIÓN EN LOTE (Opción A, B y Rollback) -->
                    <div class="dropdown-group" v-if="permissionChecker('products_edit')">
                        <button type="button" class="db-card-filter-btn dropdown-btn">
                            <i class="lab lab-line-edit lab-font-size-17"></i>
                            <span>{{ $t('button.bulk_update') }}</span>
                        </button>
                        <div class="dropdown-list db-card-filter-dropdown-list w-72">
                            <button type="button" @click="openBulkUpdateModal" class="db-card-filter-dropdown-menu flex items-center gap-2.5 py-2 px-3 w-full text-left hover:bg-slate-50 transition">
                                <span class="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-sm">
                                    <i class="lab lab-line-edit"></i>
                                </span>
                                <div>
                                    <span class="text-xs font-semibold text-slate-700 block">{{ $t('button.bulk_field_update') }}</span>
                                    <span class="text-[10px] text-slate-400 block leading-none">{{ $t('label.bulk_field_update_desc') }}</span>
                                </div>
                            </button>
                            <button type="button" @click="openCustomExcelModal" class="db-card-filter-dropdown-menu flex items-center gap-2.5 py-2 px-3 w-full text-left hover:bg-slate-50 transition border-t border-slate-100">
                                <span class="w-7 h-7 rounded-lg bg-green-100 text-green-600 flex items-center justify-center text-sm">
                                    <i class="lab lab-line-file-excel"></i>
                                </span>
                                <div>
                                    <span class="text-xs font-semibold text-slate-700 block">{{ $t('button.custom_excel') }}</span>
                                    <span class="text-[10px] text-slate-400 block leading-none">{{ $t('label.custom_excel_desc') }}</span>
                                </div>
                            </button>
                            <div class="border-t border-slate-200 mt-1 pt-1">
                                <button
                                    type="button"
                                    @click="triggerRollback"
                                    :disabled="!lastRollback || isRollingBack"
                                    class="db-card-filter-dropdown-menu flex items-center gap-2.5 py-2 px-3 w-full text-left transition"
                                    :class="lastRollback && !isRollingBack ? 'hover:bg-amber-50 text-amber-800 cursor-pointer' : 'opacity-50 cursor-not-allowed text-slate-400'"
                                >
                                    <span class="w-7 h-7 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center text-sm">
                                        <i class="fa-solid fa-rotate-left"></i>
                                    </span>
                                    <div class="truncate">
                                        <span class="text-xs font-semibold block truncate">
                                            {{ lastRollback ? $t('button.undo_last_change') : $t('button.no_rollback_available') }}
                                        </span>
                                        <span v-if="lastRollback" class="text-[10px] text-amber-600/80 block truncate leading-none">
                                            {{ lastRollback.description }} ({{ lastRollback.created_at }})
                                        </span>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>

                    <router-link v-if="aiImportEnabled" :to="{ name: 'admin.products.aiImport' }" class="db-btn h-9 py-2 px-3 bg-gray-200 hover:opacity-80">
                        <i class="lab lab-line-upload-file"></i>
                        <span>{{ $t('button.ai_import') }}</span>
                    </router-link>

                    <ProductCreateComponent :props="props" v-if="permissionChecker('products_create')" />

                    <button
                        type="button"
                        @click="refreshList"
                        class="db-btn h-9 py-2 px-3 bg-gray-200 hover:opacity-80"
                        :disabled="isRefreshingList"
                        :class="{ 'disabled:opacity-50': isRefreshingList }"
                    >
                        <template v-if="isRefreshingList">
                            <svg class="inline-block animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                            </svg>
                        </template>
                        <template v-else>
                            <i class="fa-solid fa-arrows-rotate"></i>
                        </template>
                    </button>
                </div>
            </div>

            <div class="table-filter-div" id="product-filter">
                <form class="p-4 sm:p-5 border-t" @submit.prevent="search">
                    <div class="row pt-2">
                        <div class="col-12 sm:col-6 md:col-4 xl:col-3 !pt-0">
                            <label for="searchName" class="db-field-title after:hidden">
                                {{ $t("label.name") }}
                            </label>
                            <input id="searchName" v-model="props.search.name" type="text" class="db-field-control" />
                        </div>

                        <div class="col-12 sm:col-6 md:col-4 xl:col-3 !pt-0">
                            <label for="searchSku" class="db-field-title after:hidden">
                                {{ $t("label.sku") }}
                            </label>
                            <input id="searchSku" v-model="props.search.sku" type="text" class="db-field-control" />
                        </div>

                        <div class="col-12 sm:col-6 md:col-4 xl:col-3 !pt-0">
                            <label for="searchBuyingPrice" class="db-field-title after:hidden">
                                {{ $t("label.buying_price") }}
                            </label>
                            <input id="searchBuyingPrice" v-on:keypress="numberOnly($event)"
                                v-model="props.search.buying_price" type="text" class="db-field-control" />
                        </div>

                        <div class="col-12 sm:col-6 md:col-4 xl:col-3 !pt-0">
                            <label for="searchSellingPrice" class="db-field-title after:hidden">
                                {{ $t("label.selling_price") }}
                            </label>
                            <input id="searchSellingPrice" v-on:keypress="numberOnly($event)"
                                v-model="props.search.selling_price" type="text" class="db-field-control" />
                        </div>

                        <div class="col-12 sm:col-6 md:col-4 xl:col-3 !pt-0">
                            <label for="searchCategory" class="db-field-title">
                                {{ $t("label.category") }}
                            </label>
                            <vue-select class="db-field-control f-b-custom-select" id="searchCategory"
                                v-model="props.search.product_category_ids" :options="productCategories"
                                label-by="option" value-by="id" :closeOnSelect="true" :searchable="true"
                                :clearOnClose="true" placeholder="--" search-placeholder="--" :multiple="true">
                                <template #label="{ selected }">
                                    <input
                                        :value="selectedCategoryNames(selected)"
                                        :placeholder="$t('label.category')"
                                        autocomplete="off"
                                        readonly
                                    >
                                </template>
                            </vue-select>
                        </div>

                        <div class="col-12 sm:col-6 md:col-4 xl:col-3 !pt-0">
                            <label for="searchBrand" class="db-field-title">
                                {{ $t("label.brand") }}
                            </label>
                            <vue-select class="db-field-control f-b-custom-select" id="searchBrand"
                                v-model="props.search.product_brand_id" :options="productBrands" label-by="name"
                                value-by="id" :closeOnSelect="true" :searchable="true" :clearOnClose="true"
                                placeholder="--" search-placeholder="--" />
                        </div>

                        <div class="col-12 sm:col-6 md:col-4 xl:col-3 !pt-0">
                            <label for="searchBarcode" class="db-field-title">
                                {{ $t("label.barcode") }}
                            </label>
                            <vue-select class="db-field-control f-b-custom-select" id="searchBarcode"
                                v-model="props.search.barcode_id" :options="barcodes" label-by="name" value-by="id"
                                :closeOnSelect="true" :searchable="true" :clearOnClose="true" placeholder="--"
                                search-placeholder="--" />
                        </div>

                        <div class="col-12 sm:col-6 md:col-4 xl:col-3 !pt-0">
                            <label for="searchTax" class="db-field-title">
                                {{ $t("label.tax") }}
                            </label>

                            <vue-select class="db-field-control f-b-custom-select" id="searchTax"
                                v-model="props.search.tax_id" :options="taxes" label-by="name" value-by="id"
                                :closeOnSelect="true" :searchable="true" :clearOnClose="true" placeholder="--"
                                search-placeholder="--" />
                        </div>

                        <div class="col-12 sm:col-6 md:col-4 xl:col-3 !pt-0">
                            <label for="searchUnit" class="db-field-title">
                                {{ $t("label.unit") }}
                            </label>

                            <vue-select class="db-field-control f-b-custom-select" id="searchUnit"
                                v-model="props.search.unit_id" :options="units" label-by="name_code" value-by="id"
                                :closeOnSelect="true" :searchable="true" :clearOnClose="true" placeholder="--"
                                search-placeholder="--" />
                        </div>

                        <div class="col-12 sm:col-6 md:col-4 xl:col-3 !pt-0">
                            <label for="searchStatus" class="db-field-title after:hidden">
                                {{ $t("label.status") }}
                            </label>
                            <vue-select class="db-field-control f-b-custom-select" id="searchStatus"
                                v-model="props.search.status" :options="[
                                    { id: enums.statusEnum.ACTIVE, name: $t('label.active') },
                                    { id: enums.statusEnum.INACTIVE, name: $t('label.inactive') },
                                ]" label-by="name" value-by="id" :closeOnSelect="true" :searchable="true"
                                :clearOnClose="true" placeholder="--" search-placeholder="--" />
                        </div>

                        <div class="col-12 sm:col-6 md:col-4 xl:col-3 !pt-0">
                            <label for="searchPurchasable" class="db-field-title after:hidden">
                                {{ $t("label.purchasable") }}
                            </label>
                            <vue-select class="db-field-control f-b-custom-select" id="searchPurchasable"
                                v-model="props.search.can_purchasable" :options="[
                                    { id: enums.askEnum.YES, name: $t('label.yes') },
                                    { id: enums.askEnum.NO, name: $t('label.no') },
                                ]" label-by="name" value-by="id" :closeOnSelect="true" :searchable="true"
                                :clearOnClose="true" placeholder="--" search-placeholder="--" />
                        </div>

                        <div class="col-12 sm:col-6 md:col-4 xl:col-3 !pt-0">
                            <label for="searchIsStockOut" class="db-field-title after:hidden">
                                {{ $t("label.show_stock_out") }}
                            </label>
                            <vue-select class="db-field-control f-b-custom-select" id="searchIsStockOut"
                                v-model="props.search.show_stock_out" :options="[
                                    { id: enums.activityEnum.ENABLE, name: $t('label.enable') },
                                    { id: enums.activityEnum.DISABLE, name: $t('label.disable') },
                                ]" label-by="name" value-by="id" :closeOnSelect="true" :searchable="true"
                                :clearOnClose="true" placeholder="--" search-placeholder="--" />
                        </div>

                        <div class="col-12 sm:col-6 md:col-4 xl:col-3 !pt-0">
                            <label for="searchRefundable" class="db-field-title after:hidden">
                                {{ $t("label.refundable") }}
                            </label>
                            <vue-select class="db-field-control f-b-custom-select" id="searchRefundable"
                                v-model="props.search.refundable" :options="[
                                    { id: enums.askEnum.YES, name: $t('label.yes') },
                                    { id: enums.askEnum.NO, name: $t('label.no') },
                                ]" label-by="name" value-by="id" :closeOnSelect="true" :searchable="true"
                                :clearOnClose="true" placeholder="--" search-placeholder="--" />
                        </div>
                            
                        <div class="col-12 border-t mt-1">
                            <div class="flex flex-wrap justify-end gap-2">
                                <button class="db-btn py-2 px-3 bg-gray-200" @click.prevent="handleSlide('product-filter')">
                                    <i class="lab lab-fill-close"></i>
                                </button>
                                <button class="db-btn py-2 bg-gray-200" @click="clear">
                                    <span>{{ $t('button.restore') }}</span>
                                </button>
                                <button class="db-btn py-2 text-white bg-primary">
                                    <span>{{ $t('button.apply_filter') }}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>

            <div class="db-table-responsive">
                <table class="db-table stripe" id="print">
                    <thead class="db-table-head">
                        <tr class="db-table-head-tr">
                            <th class="db-table-head-th w-10 text-center" v-if="permissionChecker('products_edit')">
                                <input
                                    type="checkbox"
                                    :checked="isAllSelected"
                                    @change="toggleSelectAll"
                                    class="rounded text-primary focus:ring-primary w-4 h-4 cursor-pointer"
                                />
                            </th>
                            <th class="db-table-head-th">
                                {{ $t('label.name') }}
                            </th>
                            <th class="db-table-head-th">
                                {{ $t('label.category') }}
                            </th>
                            <th class="db-table-head-th text-center">
                                {{ $t('label.buying_price') }}
                            </th>
                            <th class="db-table-head-th text-center">
                                {{ $t('label.selling_price') }}
                            </th>
                            <th class="db-table-head-th text-center">
                                {{ $t('label.stock') || 'Stock' }}
                            </th>
                            <th class="db-table-head-th text-center">
                                {{ $t('label.status') }}
                            </th>
                            <th class="db-table-head-th hidden-print text-right"
                                v-if="permissionChecker('products_show') || permissionChecker('products_edit') || permissionChecker('products_delete')">
                                {{ $t('label.action') }}
                            </th>
                        </tr>
                    </thead>
                    <tbody class="db-table-body" v-if="products.length > 0">
                        <tr class="db-table-body-tr" v-for="product in products" :key="product.id">
                            <td class="db-table-body-td text-center" v-if="permissionChecker('products_edit')">
                                <input
                                    type="checkbox"
                                    :value="product.id"
                                    v-model="selectedProductIds"
                                    class="rounded text-primary focus:ring-primary w-4 h-4 cursor-pointer"
                                />
                            </td>
                            <td class="db-table-body-td font-medium text-slate-800">
                                {{ textShortener(product.name, 40) }}
                                <div class="text-[11px] text-slate-400 font-normal">SKU: {{ product.sku }}</div>
                            </td>
                            <td class="db-table-body-td">{{ product.category_name }}</td>
                            <td class="db-table-body-td text-center">
                                <InlineEditableCell
                                    v-if="permissionChecker('products_edit')"
                                    :value="product.buying_price"
                                    field="buying_price"
                                    type="number"
                                    label="Precio de Compra"
                                    @update-value="handleInlineUpdate(product, $event)"
                                >
                                    {{ product.flat_buying_price }}
                                </InlineEditableCell>
                                <span v-else>{{ product.flat_buying_price }}</span>
                            </td>
                            <td class="db-table-body-td text-center">
                                <InlineEditableCell
                                    v-if="permissionChecker('products_edit')"
                                    :value="product.selling_price"
                                    field="selling_price"
                                    type="number"
                                    label="Precio de Venta"
                                    @update-value="handleInlineUpdate(product, $event)"
                                >
                                    {{ product.flat_selling_price }}
                                </InlineEditableCell>
                                <span v-else>{{ product.flat_selling_price }}</span>
                            </td>
                            <td class="db-table-body-td text-center">
                                <InlineEditableCell
                                    v-if="permissionChecker('products_edit')"
                                    :value="product.stock"
                                    field="stock"
                                    type="number"
                                    label="Stock"
                                    @update-value="handleInlineUpdate(product, $event)"
                                >
                                    <span class="font-semibold" :class="product.stock > 0 ? 'text-slate-800' : 'text-red-500'">
                                        {{ product.stock }}
                                    </span>
                                </InlineEditableCell>
                                <span v-else>{{ product.stock }}</span>
                            </td>
                            <td class="db-table-body-td text-center">
                                <InlineEditableCell
                                    v-if="permissionChecker('products_edit')"
                                    :value="product.status"
                                    field="status"
                                    type="select"
                                    :options="[
                                        { value: enums.statusEnum.ACTIVE, label: $t('label.active') },
                                        { value: enums.statusEnum.INACTIVE, label: $t('label.inactive') }
                                    ]"
                                    label="Estado"
                                    @update-value="handleInlineUpdate(product, $event)"
                                >
                                    <span :class="statusClass(product.status)">
                                        {{ enums.statusEnumArray[product.status] }}
                                    </span>
                                </InlineEditableCell>
                                <span v-else :class="statusClass(product.status)">
                                    {{ enums.statusEnumArray[product.status] }}
                                </span>
                            </td>
                            <td class="db-table-body-td hidden-print"
                                v-if="permissionChecker('products_show') || permissionChecker('products_edit') || permissionChecker('products_delete')">
                                <div class="flex justify-end items-center sm:items-start sm:justify-end gap-1.5">
                                    <SmIconCloneComponent @click="clone(product.id)"
                                        v-if="permissionChecker('products_create')" />
                                    <SmIconViewComponent :link="'admin.product.show'" :id="product.id"
                                        v-if="permissionChecker('products_show')" />
                                    <SmIconSidebarModalEditComponent @click="edit(product)"
                                        v-if="permissionChecker('products_edit')" />
                                    <SmIconDeleteComponent @click="destroy(product.id)"
                                        v-if="permissionChecker('products_delete')" />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="flex items-center justify-between border-t px-4 py-4">
                <PaginationSMBox :pagination="pagination" :method="list" />
                <div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <PaginationTextComponent :props="{ page: paginationPage }" />
                    <PaginationBox :pagination="pagination" :method="list" />
                </div>
            </div>
        </div>
    </div>
    <ProductFileUploadComponent />
    <ProductBulkUpdateModal
        :selected-product-ids="selectedProductIds"
        :products="products"
        :categories="productCategories"
        :brands="productBrands"
        @update-selected-ids="selectedProductIds = $event"
        @submit-batch="handleBatchUpdate"
    />
    <ProductCustomExcelModal
        :search-filters="props.search"
        @export-custom="handleCustomExport"
        @import-custom="handleCustomImport"
    />
</template>
<script>
import LoadingComponent from "../components/LoadingComponent";
import ProductCreateComponent from "./ProductCreateComponent";
import alertService from "../../../services/alertService";
import statusEnum from "../../../enums/modules/statusEnum";
import askEnum from "../../../enums/modules/askEnum";
import PaginationTextComponent from "../components/pagination/PaginationTextComponent";
import PaginationBox from "../components/pagination/PaginationBox";
import PaginationSMBox from "../components/pagination/PaginationSMBox";
import appService from "../../../services/appService";
import TableLimitComponent from "../components/TableLimitComponent";
import SmIconSidebarModalEditComponent from "../components/buttons/SmIconSidebarModalEditComponent";
import SmIconDeleteComponent from "../components/buttons/SmIconDeleteComponent";
import SmIconViewComponent from "../components/buttons/SmIconViewComponent";
import SmIconCloneComponent from "../components/buttons/SmIconCloneComponent";
import FilterComponent from "../components/buttons/collapse/FilterComponent";
import ExportComponent from "../components/buttons/export/ExportComponent";
import PrintComponent from "../components/buttons/export/PrintComponent";
import ExcelComponent from "../components/buttons/export/ExcelComponent";
import activityEnum from "../../../enums/modules/activityEnum";
import _ from "lodash";
import ImportComponent from "../components/buttons/import/ImportComponent";
import ProductFileUploadComponent from "./ProductFileUploadComponent";
import SampleFileComponent from "../components/buttons/import/SampleFileComponent";
import UploadFileComponent from "../components/buttons/import/UploadFileComponent";
import ProductBulkUpdateModal from "./ProductBulkUpdateModal";
import ProductCustomExcelModal from "./ProductCustomExcelModal";
import InlineEditableCell from "./InlineEditableCell";

export default {
    name: "ProductListComponent",
    components: {
        TableLimitComponent,
        PaginationSMBox,
        PaginationBox,
        PaginationTextComponent,
        ProductCreateComponent,
        LoadingComponent,
        SmIconSidebarModalEditComponent,
        SmIconDeleteComponent,
        SmIconViewComponent,
        SmIconCloneComponent,
        FilterComponent,
        ExportComponent,
        PrintComponent,
        ExcelComponent,
        ProductFileUploadComponent,
        ImportComponent,
        SampleFileComponent,
        UploadFileComponent,
        ProductBulkUpdateModal,
        ProductCustomExcelModal,
        InlineEditableCell
    },
    data() {
        return {
            loading: {
                isActive: false
            },
            enums: {
                statusEnum: statusEnum,
                askEnum: askEnum,
                activityEnum: activityEnum,
                statusEnumArray: {
                    [statusEnum.ACTIVE]: this.$t("label.active"),
                    [statusEnum.INACTIVE]: this.$t("label.inactive")
                },
            },
            aiImportEnabled: true,
            printLoading: true,
            printObj: {
                id: "print",
                popTitle: this.$t("menu.products"),
            },
            searchProps: {
                paginate: 0,
                order_column: 'id',
                order_type: 'asc'
            },
            props: {
                form: {
                    name: "",
                    sku: "",
                    product_category_id: null,
                    product_category_ids: [],
                    barcode_id: null,
                    buying_price: "",
                    selling_price: "",
                    wholesale_price: "",
                    tax_id: [],
                    product_brand_id: null,
                    status: statusEnum.ACTIVE,
                    can_purchasable: askEnum.NO,
                    show_stock_out: activityEnum.DISABLE,
                    wholesale: askEnum.NO,
                    wholesale_min: "",
                    refundable: askEnum.NO,
                    maximum_purchase_quantity: "",
                    low_stock_quantity_warning: "",
                    unit_id: null,
                    weight: "",
                    length: "",
                    width: "",
                    height: "",
                    warranty: "",
                    tags: "",
                    convertTags: [],
                    short_description: "",
                    description: "",
                    custom_fields: [],
                },
                search: {
                    paginate: 1,
                    page: 1,
                    per_page: 10,
                    order_column: 'id',
                    order_type: 'desc',
                    name: "",
                    sku: "",
                    buying_price: "",
                    selling_price: "",
                    product_category_id: null,
                    product_category_ids: [],
                    product_brand_id: null,
                    barcode_id: null,
                    tax_id: null,
                    unit_id: null,
                    status: null,
                    can_purchasable: null,
                    show_stock_out: null,
                    refundable: null
                }
            },
            isRefreshingList: false,
            productCategories: [],
            selectedProductIds: [],
            isRollingBack: false,
        }
    },
    computed: {
        products: function () {
            return this.$store.getters['product/lists'];
        },
        pagination: function () {
            return this.$store.getters['product/pagination'];
        },
        paginationPage: function () {
            return this.$store.getters['product/page'];
        },
        productBrands: function () {
            return this.$store.getters['productBrand/lists'];
        },
        taxes: function () {
            return this.$store.getters['tax/lists'];
        },
        units: function () {
            return this.$store.getters['unit/lists'];
        },
        barcodes: function () {
            return this.$store.getters['barcode/lists'];
        },
        lastRollback: function () {
            return this.$store.getters['bulkUpdate/lastRollback'];
        },
        isAllSelected: function () {
            if (!this.products || this.products.length === 0) return false;
            return this.products.every(p => this.selectedProductIds.includes(p.id));
        }
    },
    mounted() {
        this.list();
        this.fetchAiImportFlag();
        this.$store.dispatch('productCategory/depthTrees').then((res) => {
            this.productCategories = res.data.data;
            this.loading.isActive = false;
        }).catch((error) => {
            this.loading.isActive = false;
        });
        this.$store.dispatch('productBrand/lists', this.searchProps);
        this.$store.dispatch('tax/lists', this.searchProps);
        this.$store.dispatch('unit/lists', this.searchProps);
        this.$store.dispatch('barcode/lists', this.searchProps);
        this.$store.dispatch('bulkUpdate/getOptions').catch(() => {});
    },
    methods: {
        fetchAiImportFlag() {
            this.$store.dispatch('aiImportSetting/importSetting').then((res) => {
                this.aiImportEnabled = !!res?.data?.data?.is_enabled;
            }).catch(() => {
                this.aiImportEnabled = true;
            });
        },
        permissionChecker(e) {
            return appService.permissionChecker(e);
        },
        statusClass: function (status) {
            return appService.statusClass(status);
        },
        textShortener: function (text, number = 30) {
            return appService.textShortener(text, number);
        },
        handleSlide: function (id) {
            return appService.handleSlide(id);
        },
        numberOnly: function (e) {
            return appService.floatNumber(e);
        },
        search: function () {
            this.list();
        },
        clear: function () {
            this.props.search.paginate = 1;
            this.props.search.page = 1;
            this.props.search.name = "";
            this.props.search.sku = "";
            this.props.search.buying_price = "";
            this.props.search.selling_price = "";
            this.props.search.product_category_id = null;
            this.props.search.product_category_ids = [];
            this.props.search.product_brand_id = null;
            this.props.search.barcode_id = null;
            this.props.search.tax_id = null;
            this.props.search.unit_id = null;
            this.props.search.show_stock_out = null;
            this.props.search.status = null;
            this.props.search.can_purchasable = null;
            this.props.search.refundable = null;
            this.list();
        },
        list: function (page = 1, { silent = false } = {}) {
            if (!silent) {
                this.loading.isActive = true;
            }
            this.props.search.page = page;
            this.props.search.product_category_ids = this.normalizeCategoryIds(this.props.search.product_category_ids);
            this.props.search.product_category_id = this.props.search.product_category_ids.length
                ? this.props.search.product_category_ids[0]
                : null;
            return this.$store.dispatch('product/lists', this.props.search).then(res => {
                if (!silent) {
                    this.loading.isActive = false;
                }
            }).catch((err) => {
                if (!silent) {
                    this.loading.isActive = false;
                }
            });
        },
        async refreshList() {
            if (this.isRefreshingList) {
                return;
            }

            this.isRefreshingList = true;
            try {
                await this.list(this.props.search.page || 1, { silent: true });
            } finally {
                this.isRefreshingList = false;
            }
        },
        edit: function (product) {
            this.loading.isActive = true;
            appService.sideDrawerShow();
            this.$store.dispatch('product/edit', product.id);
            this.loading.isActive = false;

            this.props.form.name = product.name;
            this.props.form.sku = product.sku;
            this.props.form.product_category_id = product.product_category_id;
            this.props.form.product_category_ids = this.categoryUpdate(product.product_category_ids, product.product_category_id);
            this.props.form.barcode_id = product.barcode_id;
            this.props.form.buying_price = product.flat_buying_price;
            this.props.form.selling_price = product.flat_selling_price;
            this.props.form.wholesale_price = product.flat_wholesale_price;
            this.props.form.tax_id = this.taxUpdate(product.tax_id);
            this.props.form.product_brand_id = product.product_brand_id;
            this.props.form.status = product.status;
            this.props.form.can_purchasable = product.can_purchasable;
            this.props.form.show_stock_out = product.show_stock_out;
            this.props.form.wholesale = product.wholesale;
            this.props.form.wholesale_min = product.wholesale_min;
            this.props.form.refundable = product.refundable;
            this.props.form.maximum_purchase_quantity = product.maximum_purchase_quantity;
            this.props.form.low_stock_quantity_warning = product.low_stock_quantity_warning;
            this.props.form.unit_id = product.unit_id;
            this.props.form.weight = product.weight;
            this.props.form.length = product.length;
            this.props.form.width = product.width;
            this.props.form.height = product.height;
            this.props.form.warranty = product.warranty;
            this.props.form.convertTags = this.tagUpdate(product.product_tags);
            this.props.form.tags = ""
            this.props.form.short_description = product.short_description;
            this.props.form.description = product.description;
            this.props.form.custom_fields = this.customFieldUpdate(product.custom_fields);
        },
        tagUpdate: function (objects) {
            let tags = [];
            _.forEach(objects, (object) => {
                tags.push({ "text": object.name, "tiClasses": ["ti-valid"] });
            });
            return tags;
        },
        taxUpdate: function (objects) {
            let taxes = [];
            _.forEach(objects, (object, key) => {
                taxes.push(object.tax_id);
            });
            return taxes;
        },
        normalizeCategoryIds: function (rows) {
            if (!Array.isArray(rows)) {
                return [];
            }

            return [...new Set(rows
                .map((id) => parseInt(id))
                .filter((id) => Number.isInteger(id) && id > 0))];
        },
        selectedCategoryNames: function (selected) {
            if (!Array.isArray(selected) || selected.length === 0) {
                return "";
            }

            return selected
                .map((category) => category?.option ?? category?.name ?? "")
                .filter((name) => name !== "")
                .join(", ");
        },
        categoryUpdate: function (objects, fallbackId = null) {
            if (Array.isArray(objects) && objects.length) {
                return objects.map((id) => parseInt(id)).filter((id) => Number.isInteger(id) && id > 0);
            }

            const parsedFallback = parseInt(fallbackId);
            if (Number.isInteger(parsedFallback) && parsedFallback > 0) {
                return [parsedFallback];
            }

            return [];
        },
        customFieldUpdate: function (objects) {
            if (Array.isArray(objects)) {
                return objects.map((object) => {
                    const key = `${object?.key ?? ''}`.trim();
                    return {
                        uid: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
                        key,
                        label: `${object?.label ?? key}`,
                        value: `${object?.value ?? ''}`,
                    };
                });
            }

            if (!objects || typeof objects !== 'object') {
                return [];
            }

            // Backward compatibility: convert key-value objects into editable rows.
            return Object.entries(objects).map(([key, value]) => {
                return {
                    uid: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
                    key: `${key}`,
                    label: `${key}`,
                    value: `${value ?? ''}`,
                };
            });
        },
        clone: function (id) {
            appService.cloneConfirmation().then((res) => {
                try {
                    this.loading.isActive = true;
                    this.$store.dispatch('product/clone', { id: id, search: this.props.search }).then((res) => {
                        this.loading.isActive = false;
                        alertService.successFlip(null, this.$t('menu.products'));
                    }).catch((err) => {
                        this.loading.isActive = false;
                        alertService.error(err.response.data.message);
                    })
                } catch (err) {
                    this.loading.isActive = false;
                    alertService.error(err.response.data.message);
                }
            }).catch((err) => {
                this.loading.isActive = false;
            })
        },
        destroy: function (id) {
            appService.destroyConfirmation().then((res) => {
                try {
                    this.loading.isActive = true;
                    this.$store.dispatch('product/destroy', { id: id, search: this.props.search }).then((res) => {
                        this.loading.isActive = false;
                        alertService.successFlip(null, this.$t('menu.products'));
                    }).catch((err) => {
                        this.loading.isActive = false;
                        alertService.error(err.response.data.message);
                    })
                } catch (err) {
                    this.loading.isActive = false;
                    alertService.error(err.response.data.message);
                }
            }).catch((err) => {
                this.loading.isActive = false;
            })
        },
        xls: function () {
            this.props.search.product_category_ids = this.normalizeCategoryIds(this.props.search.product_category_ids);
            this.props.search.product_category_id = this.props.search.product_category_ids.length
                ? this.props.search.product_category_ids[0]
                : null;

            this.loading.isActive = true;
            this.$store.dispatch("product/export", this.props.search).then((res) => {
                this.loading.isActive = false;
                const blob = new Blob([res.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = this.$t("menu.products");
                link.click();
                URL.revokeObjectURL(link.href);
            }).catch((err) => {
                this.loading.isActive = false;
                alertService.error(err.response.data.message);
            });
        },
        download: function () {
            this.loading.isActive = true;
            this.$store.dispatch("product/download").then((res) => {
                this.loading.isActive = false;
                const blob = new Blob([res.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = "PlantillaImportacionProductos.xlsx";
                link.click();
                URL.revokeObjectURL(link.href);
            }).catch((err) => {
                this.loading.isActive = false;
                alertService.error(err.response.data.message);
            });
        },
        toggleSelectAll: function () {
            if (this.isAllSelected) {
                this.selectedProductIds = [];
            } else {
                this.selectedProductIds = (this.products || []).map(p => p.id);
            }
        },
        openBulkUpdateModal: function () {
            appService.modalShow('#productBulkUpdateModal');
        },
        openCustomExcelModal: function () {
            appService.modalShow('#productCustomExcelModal');
        },
        handleBatchUpdate: function ({ payload, onDone, onError }) {
            this.$store.dispatch('bulkUpdate/batchUpdate', payload).then((res) => {
                if (typeof onDone === 'function') onDone();
                alertService.success(res.data.message || 'Actualización masiva completada.');
                this.selectedProductIds = [];
                this.list(this.props.search.page || 1, { silent: true });
            }).catch((err) => {
                if (typeof onError === 'function') onError(err);
                alertService.error(err.response?.data?.message || 'Error al aplicar actualización.');
            });
        },
        handleCustomExport: function ({ filters, columns, onDone }) {
            this.$store.dispatch('bulkUpdate/exportCustom', { filters, columns }).then((res) => {
                if (typeof onDone === 'function') onDone();
                const blob = new Blob([res.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = "ActualizacionProductosPersonalizada.xlsx";
                link.click();
                URL.revokeObjectURL(link.href);
            }).catch((err) => {
                if (typeof onDone === 'function') onDone();
                alertService.error(err.response?.data?.message || 'Error al exportar plantilla.');
            });
        },
        handleCustomImport: function ({ formData, onDone }) {
            this.$store.dispatch('bulkUpdate/importCustom', formData).then((res) => {
                if (typeof onDone === 'function') onDone();
                alertService.success(res.data.message || 'Importación completada correctamente.');
                this.list(this.props.search.page || 1, { silent: true });
            }).catch((err) => {
                if (typeof onDone === 'function') onDone();
                alertService.error(err.response?.data?.message || 'Error al procesar el archivo Excel.');
            });
        },
        handleInlineUpdate: function (product, { field, value, done }) {
            this.$store.dispatch('bulkUpdate/quickUpdate', {
                productId: product.id,
                field: field,
                value: value
            }).then((res) => {
                done(true);
                alertService.success('Campo actualizado.');
                this.list(this.props.search.page || 1, { silent: true });
            }).catch((err) => {
                done(false);
                alertService.error(err.response?.data?.message || 'Error al actualizar celda.');
            });
        },
        triggerRollback: function () {
            if (!this.lastRollback) return;

            appService.confirmation({
                title: '¿Revertir último lote?',
                text: `Se restaurarán los valores previos: ${this.lastRollback.description}.`,
                confirmButtonText: 'Sí, revertir cambios',
                confirmButtonColor: '#d97706'
            }).then(() => {
                this.isRollingBack = true;
                this.loading.isActive = true;
                this.$store.dispatch('bulkUpdate/rollback', this.lastRollback.id).then((res) => {
                    this.isRollingBack = false;
                    this.loading.isActive = false;
                    alertService.success(res.data.message || 'Cambios revertidos exitosamente.');
                    this.list(this.props.search.page || 1, { silent: true });
                }).catch((err) => {
                    this.isRollingBack = false;
                    this.loading.isActive = false;
                    alertService.error(err.response?.data?.message || 'Error al revertir.');
                });
            }).catch(() => {});
        }
    }
}
</script>

<style scoped>
@media print {
    .hidden-print {
        display: none !important;
    }
}
</style>